import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSeoForPath, SITE_URL, SITE_NAME } from "@/lib/seo";

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  document.head
    .querySelectorAll(`meta[${attr}="${key}"]`)
    .forEach((el) => el.remove());
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeCanonical() {
  document.head
    .querySelectorAll('link[rel="canonical"]')
    .forEach((el) => el.remove());
}

export default function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);

    if (!seo) {
      // Non-indexable / unknown route: mark noindex and avoid leaking metadata.
      document.title = `Page Not Found | ${SITE_NAME}`;
      setMeta("name", "description", "The page you requested could not be found.");
      setMeta("name", "robots", "noindex, nofollow");
      removeMeta("name", "keywords");
      removeCanonical();
      return;
    }

    const canonical = seo.canonicalPath
      ? `${SITE_URL}${seo.canonicalPath}`
      : SITE_URL;

    document.title = seo.title;
    setMeta("name", "description", seo.description);
    setMeta("name", "keywords", seo.keywords);
    setCanonical(canonical);

    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", seo.ogType);
    setMeta("property", "og:site_name", SITE_NAME);
    if (seo.image) {
      setMeta("property", "og:image", seo.image);
    } else {
      removeMeta("property", "og:image");
    }

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    if (seo.image) {
      setMeta("name", "twitter:image", seo.image);
    } else {
      removeMeta("name", "twitter:image");
    }
  }, [pathname]);

  return null;
}